/**
 * Which launcher actions the CONSOLE actually resolves (#2328 §3, B3 ruling §4).
 *
 * Pure, and exported so the omissions can be proven without a renderer. The
 * kit's vocabulary says which kinds EXIST; this says which of them this
 * deployment has somewhere to send a person, and it is the second half that
 * #2328 keeps insisting on: *"only when a real destination exists"*, *"Do not
 * render placeholder/dead tiles"*, *"If no valid contact destination is
 * configured, omit the tile and render no gap or dead action."*
 *
 * ── MEASURED, on this tree, today (rule 43 — re-measured, not inherited) ──
 *
 * | kind | destination | state |
 * |---|---|---|
 * | Discover | `/modules`, `router.tsx` | RESOLVES |
 * | Platform settings | `/settings`, `router.tsx` | RESOLVES, capability-gated |
 * | About OnMarble | the launcher's own governed panel | RESOLVES |
 * | What's new | — | OMITTED: #2329 is the single governed customer What's new destination and it is a PORTAL surface. The console has no update destination at all; `apps/portal/src/pages/News.tsx` is the customer one |
 * | Help | — | OMITTED: there is no governed help destination anywhere in this repo. Re-measured: zero hits for a help/support route, setting or door |
 * | Contact Moonbase | — | OMITTED: no registered contact destination exists. The code path is live and exercised — pass a governed HTTPS destination and the tile appears — so the omission is a fact about the deployment, not a hole in the shell |
 *
 * Three of six, and that is the design working rather than an unfinished
 * launcher: a tile is an assertion that somewhere real exists behind it.
 *
 * ── THE SETTINGS GATE, AND WHAT IT HONESTLY IS ───────────────────────────
 * §3 says Platform settings is *"Console only and capability-gated"*, and §10's
 * permission-boundary journey wants the tile ABSENT for a principal without the
 * capability. The capability `apps/console/src/settings/registry.ts` declares is
 * `platform.admin` — and MEASURED: no door in this tree answers *"do I hold
 * platform.admin"* to a client. `core.table_presentation_authoring_allowed()`
 * returns exactly that boolean and is named after a different question, so
 * binding the Settings tile to it would be a rename away from silent breakage.
 *
 * So the gate here is the one the shell ALREADY enforces for `/settings`: the
 * `backstage` area from `access.my_areas()`, which is what `Nav.tsx` uses to
 * decide whether the Settings group exists at all and what `AppShell` uses to
 * bounce a customer-only principal off backstage routes. A customer-only
 * principal gets no tile, which is the boundary journey. A backstage principal
 * who is not a platform admin still sees it — and §8 is explicit that *"UI
 * omission is not authority; every settings read/write remains
 * server-authorized"*. The missing door is REPORTED as a core-shaped gap rather
 * than faked here, and `capabilityHeld` is the one line that changes when it
 * lands.
 */
import type { LauncherAction } from '@solum/ui'

export interface ConsoleLauncherContext {
  /** Navigate to a console route. The host supplies its router. */
  go: (to: string) => void
  /** Open the About OnMarble panel. */
  onAbout: () => void
  /**
   * Does this principal hold the platform-settings capability?
   *
   * Today the console answers this with the `backstage` area — see the header.
   * A governed capability door replaces the ARGUMENT passed here, not this
   * file's shape.
   */
  mayReachPlatformSettings: boolean
  /**
   * The governed Moonbase contact destination, when one is configured.
   *
   * There is no registered contact destination in this tree, so this is `null`
   * at every call site today and the tile is omitted. It is a parameter rather
   * than a constant because the amendment is explicit that it *"resolves from a
   * registered contact destination with an HTTPS/route allowlist"* — a URL
   * written in this file would be exactly the free-text destination it refuses.
   */
  contactUrl?: string | null
}

/**
 * The console's offer. Every entry is a KIND plus what resolved; an entry whose
 * destination is absent is passed with nothing and the kit omits it.
 *
 * `whats-new` and `help` are not in this list AT ALL, which is a stronger
 * statement than passing them unresolved: the console has no such destination
 * to resolve, so there is nothing for a later edit to accidentally fill in with
 * a guess.
 */
export function consoleLauncherActions(ctx: ConsoleLauncherContext): LauncherAction[] {
  return [
    { kind: 'discover', onSelect: () => ctx.go('/modules') },
    {
      kind: 'platform-settings',
      onSelect: ctx.mayReachPlatformSettings ? () => ctx.go('/settings') : null,
    },
    { kind: 'about-onmarble', onSelect: ctx.onAbout },
    { kind: 'contact-moonbase', href: ctx.contactUrl ?? null },
  ]
}
